import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const EditSocialTransfers = ({ socialTransfersData, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const household_social_transfers_id =
    socialTransfersData?.household_social_transfers_id || "";

  const [formData, setFormData] = useState({
    aware_zakat_baithulmaal: "",
    received_zakat_baithulmaal: "",
    aware_bisp_ehsas: "",
    received_bisp_ehsas: "",
    aware_pension_benefits: "",
    received_pension_benefits: "",
    aware_other_assistance: "",
    received_other_assistance: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);
  const [householdInfoData, setHouseholdInfoData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Social transfer programs configuration
  const socialPrograms = [
    {
      id: "zakat_baithulmaal",
      name: "Zakat / Bait-ul-Maal",
      awareField: "aware_zakat_baithulmaal",
      receivedField: "received_zakat_baithulmaal"
    },
    {
      id: "bisp_ehsas",
      name: "BISP / Ehsas",
      awareField: "aware_bisp_ehsas",
      receivedField: "received_bisp_ehsas"
    },
    {
      id: "pension_benefits",
      name: "Retirement / Pension Benefits",
      awareField: "aware_pension_benefits",
      receivedField: "received_pension_benefits"
    },
    {
      id: "other_assistance",
      name: "Other Assistance Programmes",
      awareField: "aware_other_assistance",
      receivedField: "received_other_assistance"
    }
  ];

  const dbValueToYesNo = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (value === 1 || value === "1" || value === true) return "yes";
    if (value === 0 || value === "0" || value === false) return "no";
    return "";
  };

  const yesNoToBool = (value) => {
    return value === "yes";
  };

  useEffect(() => {
    setIsDqaApproved(DqaApproved);
    setIsDqaRejected(DqaRejected);
  }, [DqaApproved, DqaRejected]);

  useEffect(() => {
    if (socialTransfersData && Object.keys(socialTransfersData).length > 0) {
      setFormData({
        aware_zakat_baithulmaal: dbValueToYesNo(socialTransfersData.aware_zakat_baithulmaal),
        received_zakat_baithulmaal: dbValueToYesNo(socialTransfersData.received_zakat_baithulmaal),
        aware_bisp_ehsas: dbValueToYesNo(socialTransfersData.aware_bisp_ehsas),
        received_bisp_ehsas: dbValueToYesNo(socialTransfersData.received_bisp_ehsas),
        aware_pension_benefits: dbValueToYesNo(socialTransfersData.aware_pension_benefits),
        received_pension_benefits: dbValueToYesNo(socialTransfersData.received_pension_benefits),
        aware_other_assistance: dbValueToYesNo(socialTransfersData.aware_other_assistance),
        received_other_assistance: dbValueToYesNo(socialTransfersData.received_other_assistance),
      });
    } else if (socialTransfersData !== undefined) {
      // Initialize with empty strings (no selection) when no data
      setFormData({
        aware_zakat_baithulmaal: "",
        received_zakat_baithulmaal: "",
        aware_bisp_ehsas: "",
        received_bisp_ehsas: "",
        aware_pension_benefits: "",
        received_pension_benefits: "",
        aware_other_assistance: "",
        received_other_assistance: "",
      });
    }
    
    if (socialTransfersData !== undefined) {
      setDataLoaded(true);
    }
  }, [socialTransfersData]);

  const handleChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
  };

  const validate = () => {
    const e = {};
    const hasValue = (v) => v === "yes" || v === "no";

    socialPrograms.forEach(program => {
      if (!hasValue(formData[program.awareField])) {
        e[program.awareField] = "Please select Yes or No.";
      }
      if (!hasValue(formData[program.receivedField])) {
        e[program.receivedField] = "Please select Yes or No.";
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id) {
      toast.error("Household ID is missing");
      return;
    }

    if (!household_social_transfers_id) {
      toast.error("Social transfers record not found");
      return;
    }

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const convertedData = Object.fromEntries(
        Object.entries(formData).map(([key, val]) => [key, yesNoToBool(val)])
      );

      const payload = {
        household_information_id: id,
        household_social_transfers_id: household_social_transfers_id,
        ...convertedData,
      };

      const response = await fetchData(
        `household-social-transfer-update/${household_social_transfers_id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (response?.success) {
        toast.success("Social Transfers updated successfully!");
      } else {
        toast.error(response?.message || "Failed to update Social Transfers");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update Social Transfers.");
    } finally {
      setLoading(false);
    }
  };

  // Check if we have social transfers data
  const hasSocialTransfersData = socialTransfersData && Object.keys(socialTransfersData).length > 0;

  if (!dataLoaded) {
    return (
      <div className="p-4 bg-white shadow rounded">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Loading social transfers...</p>
        </div>
      </div>
    );
  }

  const Err = (name) =>
    errors?.[name] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">{errors[name]}</p>
    ) : null;

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Social Transfer
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Social Transfer
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className="w-full">
        {/* Awareness Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">
            Are you aware of the following programs?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {socialPrograms.map((program) => (
              <div key={`aware-${program.id}`}>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {program.name} {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={program.awareField}
                      checked={formData[program.awareField] === "yes"}
                      onChange={() => handleChange(program.awareField, "yes")}
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasSocialTransfersData}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={program.awareField}
                      checked={formData[program.awareField] === "no"}
                      onChange={() => handleChange(program.awareField, "no")}
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasSocialTransfersData}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err(program.awareField)}
              </div>
            ))}
          </div>
        </div>

        {/* Received Assistance Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">
            Has your household received assistance through the following
            programs?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {socialPrograms.map((program) => (
              <div key={`received-${program.id}`}>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {program.name} {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={program.receivedField}
                      checked={formData[program.receivedField] === "yes"}
                      onChange={() =>
                        handleChange(program.receivedField, "yes")
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasSocialTransfersData}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={program.receivedField}
                      checked={formData[program.receivedField] === "no"}
                      onChange={() => handleChange(program.receivedField, "no")}
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasSocialTransfersData}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err(program.receivedField)}
              </div>
            ))}
          </div>
        </div>

        {!isDqaApproved &&
          !isDqaRejected &&
          household_social_transfers_id &&
          hasSocialTransfersData && (
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className={`${
                  loading ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
                } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          )}
      </form>
    </div>
  );
};

export default EditSocialTransfers;